import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { 
  FileSpreadsheet, 
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  User,
  ArrowRight,
  Loader2,
  Search,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Blog List Page
const BlogListPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      // Fetch only published posts
      const response = await api.get('/api/blog/posts?status=published');
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post =>
    post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Separate pillars and clusters
  const pillarPosts = filteredPosts.filter(p => p.type === 'pillar');
  const clusterPosts = filteredPosts.filter(p => p.type === 'cluster');

  // Build URL for a post
  const getPostUrl = (post) => {
    if (post.type === 'pillar') {
      return `/blog/${post.slug}`;
    }
    // For clusters, find the parent pillar
    const pillar = posts.find(p => p.id === post.pillarPageId);
    if (pillar) {
      return `/blog/${pillar.slug}/${post.slug}`;
    }
    return `/blog/${post.slug}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-b from-indigo-50 to-white pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Blog</h1>
          <p className="text-xl text-slate-600 mb-8">
            Tips, guides, and insights for better CRM data management
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
            />
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">
                {searchTerm ? 'No articles found matching your search.' : 'No articles published yet. Check back soon!'}
              </p>
            </div>
          ) : (
            <>
              {/* Pillar Posts (Featured) */}
              {pillarPosts.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                    Complete Guides
                  </h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    {pillarPosts.map((post) => (
                      <article 
                        key={post.id}
                        className="bg-white rounded-2xl border-2 border-indigo-100 overflow-hidden hover:shadow-lg hover:border-indigo-300 transition-all group"
                      >
                        {post.featuredImage && (
                          <div className="aspect-video bg-slate-100 overflow-hidden">
                            <img 
                              src={post.featuredImage} 
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <div className="p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs font-semibold px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                              Guide Complet
                            </span>
                            {post.tags?.slice(0, 2).map((tag) => (
                              <span 
                                key={tag}
                                className="text-xs font-medium px-2 py-1 bg-slate-100 text-slate-600 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
                            <Link to={getPostUrl(post)}>
                              {post.title}
                            </Link>
                          </h2>
                          <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {post.publishedAt 
                                ? new Date(post.publishedAt).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'Draft'
                              }
                            </div>
                            <Link 
                              to={getPostUrl(post)}
                              className="text-indigo-600 font-medium hover:text-indigo-700 flex items-center gap-1"
                            >
                              Lire <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {/* Cluster Posts */}
              {clusterPosts.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">All Articles</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {clusterPosts.map((post) => {
                      const pillar = posts.find(p => p.id === post.pillarPageId);
                      return (
                        <article 
                          key={post.id}
                          className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group"
                        >
                          {post.featuredImage && (
                            <div className="aspect-video bg-slate-100 overflow-hidden">
                              <img 
                                src={post.featuredImage} 
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          )}
                          <div className="p-5">
                            {pillar && (
                              <Link 
                                to={`/blog/${pillar.slug}`}
                                className="text-xs text-indigo-600 hover:text-indigo-700 mb-2 flex items-center gap-1"
                              >
                                {pillar.title} <ChevronRight className="w-3 h-3" />
                              </Link>
                            )}
                            <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
                              <Link to={getPostUrl(post)}>
                                {post.title}
                              </Link>
                            </h3>
                            <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                              {post.excerpt}
                            </p>
                            <div className="flex items-center justify-between text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {Math.ceil((post.content?.length || 0) / 1500)} min
                              </span>
                              <Link 
                                to={getPostUrl(post)}
                                className="text-indigo-600 font-medium hover:text-indigo-700"
                              >
                                Lire →
                              </Link>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to clean your CRM data?
          </h2>
          <p className="text-indigo-100 mb-8">
            Get 100 free credits when you sign up. No credit card required.
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50">
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-indigo-500" />
            <span className="text-slate-400">© 2026 CRM Data Cleaner</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-slate-400 hover:text-white text-sm">Home</Link>
            <Link to="/features" className="text-slate-400 hover:text-white text-sm">Features</Link>
            <Link to="/pricing/subscription" className="text-slate-400 hover:text-white text-sm">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Single Blog Post Page
const BlogPostPage = () => {
  const { slug, pillarSlug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [pillar, setPillar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug, pillarSlug]);

  const fetchPost = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      // Use the by-slug endpoint with optional pillar_slug
      const url = pillarSlug 
        ? `/api/blog/by-slug/${slug}?pillar_slug=${pillarSlug}`
        : `/api/blog/by-slug/${slug}`;
      
      const response = await api.get(url);
      setPost(response.data);
      setClusters(response.data._clusters || []);
      setPillar(response.data._pillar || null);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      if (error.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Article Not Found</h1>
            <p className="text-slate-600 mb-4">The article you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/blog')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Schema.org */}
      {post?.schemaOrg && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(post.schemaOrg) }} />
      )}
      
      <Header />

      {/* Breadcrumb */}
      <div className="pt-20 px-4 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto py-4">
          <nav className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/blog" className="hover:text-indigo-600">Blog</Link>
            {pillar && (
              <>
                <ChevronRight className="w-4 h-4" />
                <Link to={`/blog/${pillar.slug}`} className="hover:text-indigo-600">
                  {pillar.title}
                </Link>
              </>
            )}
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-medium truncate">{post?.title}</span>
          </nav>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Type Badge */}
          {post?.type === 'pillar' && (
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Guide Complet
            </div>
          )}

          {/* Tags */}
          {post?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag) => (
                <span 
                  key={tag}
                  className="text-xs font-medium px-3 py-1 bg-slate-100 text-slate-600 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            {post?.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-8">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              CRM Data Cleaner Team
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {post?.publishedAt 
                ? new Date(post.publishedAt).toLocaleDateString('fr-FR', { month: 'long', day: 'numeric', year: 'numeric' })
                : 'Draft'
              }
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {Math.ceil((post?.content?.length || 0) / 1500)} min de lecture
            </div>
          </div>

          {/* Featured Image */}
          {post?.featuredImage && (
            <div className="rounded-2xl overflow-hidden mb-8">
              <img 
                src={post.featuredImage} 
                alt={post.metaTitle || post.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-slate prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-slate-900 mt-8 mb-4" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-xl font-bold text-slate-900 mt-6 mb-3" {...props} />,
                p: ({node, ...props}) => <p className="text-slate-600 mb-4 leading-relaxed" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 text-slate-600" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 text-slate-600" {...props} />,
                li: ({node, ...props}) => <li className="mb-2" {...props} />,
                a: ({node, ...props}) => <a className="text-indigo-600 hover:text-indigo-700 underline" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-indigo-500 pl-4 italic text-slate-500 my-4" {...props} />,
                code: ({node, inline, ...props}) => inline 
                  ? <code className="bg-slate-100 px-1 rounded text-sm" {...props} />
                  : <code className="block bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm" {...props} />,
                table: ({node, ...props}) => (
                  <div className="overflow-x-auto my-6">
                    <table className="min-w-full border-collapse border border-slate-200 rounded-lg" {...props} />
                  </div>
                ),
                thead: ({node, ...props}) => <thead className="bg-slate-100" {...props} />,
                tbody: ({node, ...props}) => <tbody className="divide-y divide-slate-200" {...props} />,
                tr: ({node, ...props}) => <tr className="hover:bg-slate-50" {...props} />,
                th: ({node, ...props}) => (
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900 border border-slate-200" {...props} />
                ),
                td: ({node, ...props}) => (
                  <td className="px-4 py-3 text-sm text-slate-600 border border-slate-200" {...props} />
                ),
              }}
            >
              {post?.content || ''}
            </ReactMarkdown>
          </div>

          {/* Cluster Articles (if this is a pillar) */}
          {clusters.length > 0 && (
            <div className="mt-12 p-6 bg-indigo-50 rounded-2xl">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-indigo-600" />
                Articles de ce guide
              </h3>
              <div className="grid gap-3">
                {clusters.map((cluster) => (
                  <Link
                    key={cluster.id}
                    to={`/blog/${post.slug}/${cluster.slug}`}
                    className="flex items-center justify-between p-4 bg-white rounded-lg hover:shadow-md transition-shadow group"
                  >
                    <div>
                      <h4 className="font-medium text-slate-900 group-hover:text-indigo-600">
                        {cluster.title}
                      </h4>
                      {cluster.excerpt && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">{cluster.excerpt}</p>
                      )}
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA Box */}
          <div className="mt-12 p-8 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-2xl text-center">
            <h3 className="text-xl font-bold text-white mb-2">
              Ready to clean your CRM data?
            </h3>
            <p className="text-indigo-100 mb-4">
              Try CRM Data Cleaner free with 100 credits. No credit card required.
            </p>
            <Link to="/login">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50">
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-indigo-500" />
            <span className="text-slate-400">© 2026 CRM Data Cleaner</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-slate-400 hover:text-white text-sm">Home</Link>
            <Link to="/features" className="text-slate-400 hover:text-white text-sm">Features</Link>
            <Link to="/pricing/subscription" className="text-slate-400 hover:text-white text-sm">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export { BlogListPage, BlogPostPage };
